import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalWidth, ZINDEX_MODAL } from 'utils/constants/app/ui';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { CommandNames } from 'utils/constants/swarm/swarm-command-names';
import { fireEvent } from 'utils/generic/event-bus';
import { showToastError } from 'utils/generic/show-toast-error';
import { isMobile } from 'utils/is-mobile';
import { EKycType } from 'interfaces/generic';
import { TSwarmError } from 'interfaces/spring-websocket-interfaces';
import { swarmCommand } from 'services/get-swarm-data';
import { ESignicatVerificationActions } from './types';
import { Modal, ModalButtons } from 'components/Modal';
import { Button } from 'newcomponents/UI/Button';
import { useEventbus } from 'newhooks/generic/useEventbus';
import './index.less';

const REQUEST_INTERVAL = 30000;

type TGetConfirmActionResponse = {
  ConfirmationUrl: string;
  IsConfirmed: null | boolean;
  ConfirmationUrlQr?: string;
};

const isLegitimuzVerificationType =
  SpringConfigs.KYC_VERIFICATION_TYPE === EKycType.Legitimuz;

export const SignicatVerificationBeforeActions = () => {
  const { t } = useTranslation();
  const [verificationParams, setVerificationParams] = useState<{
    shouldVerify: true;
    email?: string;
    callbackActionParams?: Record<string, any>;
  } | null>(null);

  const [confirmUrl, setConfirmUrl] = useState<string | null>(null);

  useEventbus({
    [ESignicatVerificationActions.Verify]: ({
      payload
    }: {
      payload: {
        email?: string;
        callbackActionParams?: Record<string, any>;
      };
    }) => {
      setVerificationParams({ shouldVerify: true, ...payload });
    }
  });

  useEffect(() => {
    if (verificationParams) {
      const shouldVerifyByEmail = verificationParams.email;

      const commandName = isLegitimuzVerificationType
        ? CommandNames.GET_LEGITIMUZ_ACTION_CONFIRMATION_URL
        : shouldVerifyByEmail
        ? CommandNames.GET_SIGNICAT_ACTION_CONFIRMATION_URL_BY_EMAIL
        : CommandNames.GET_SIGNICAT_ACTION_CONFIRMATION_URL;

      const requestParams = shouldVerifyByEmail
        ? { email: verificationParams.email }
        : {};

      swarmCommand(
        commandName,
        requestParams,
        ({ details }: { details: TGetConfirmActionResponse }) => {
          if (details.IsConfirmed === null && details?.ConfirmationUrl) {
            if (details.ConfirmationUrl === 'None') {
              showToastError("ConfirmationUrl is 'None'");
              setVerificationParams(null);
              fireEvent({ key: ESignicatVerificationActions.Failed });
            } else {
              if (!isMobile() && details.ConfirmationUrlQr) {
                setConfirmUrl(details.ConfirmationUrlQr);
              } else {
                setConfirmUrl(details.ConfirmationUrl);
              }
            }
          } else if (details.IsConfirmed) {
            fireEvent({
              key: ESignicatVerificationActions.Verified,
              payload: verificationParams.callbackActionParams
            });
            setVerificationParams(null);
          }
        },
        null,
        (err: TSwarmError) => {
          setVerificationParams(null);
          fireEvent({
            key: ESignicatVerificationActions.Failed,
            payload: { errMessage: err.data.result_text }
          });
        }
      );

      const interval = setInterval(() => {
        swarmCommand(
          commandName,
          requestParams,
          ({ details }: { details: TGetConfirmActionResponse }) => {
            if (details.IsConfirmed) {
              fireEvent({
                key: ESignicatVerificationActions.Verified,
                payload: verificationParams.callbackActionParams
              });
              setVerificationParams(null);
            }
          }
        );
      }, REQUEST_INTERVAL);

      return () => {
        clearInterval(interval);
      };
    }
  }, [verificationParams]);

  return (
    <Modal
      visible={!!verificationParams}
      centered
      closable={false}
      zIndex={ZINDEX_MODAL}
      maskStyle={{ zIndex: ZINDEX_MODAL - 1 }}
      width={isMobile() ? ModalWidth.MOBILE_350 : ModalWidth.DESKTOP_420}
      title={t('account.pendingVerificationModalTitle')}
      image={{
        lib: 'generic',
        name: 'socialLoading',
        theme: 'default',
        color: 'var(--v3-primary-color)'
      }}
      className="signicatVerificationModal"
    >
      <div className="signicatVerificationModal__info">
        <p>{t('account.pendingVerificationModalDesc')}</p>
      </div>
      <ModalButtons>
        <Button
          fullwidth
          loading={!confirmUrl}
          disabled={!confirmUrl}
          type="primary"
          onClick={() => {
            confirmUrl && window.open(confirmUrl, '_blank');
          }}
        >
          {t('account.verify')}
        </Button>
      </ModalButtons>
    </Modal>
  );
};
